import React, { useEffect, useMemo, useState } from 'react';
import '../styles/pages/user_analytics.css';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { usePartnerOrganizationsQuery } from '@hooks/requests/queries/use_partner_organizations_query';
import { usePartnerOrganizationQuery } from '@hooks/requests/queries/use_partner_organization_query';
import { usePartnerCompaniesAllQuery } from '@hooks/requests/queries/use_partner_companies_all_query';
import { useAllUsersQuery } from '@hooks/requests/queries/use_all_users_query';
import {
  useBillingTrendQuery,
  scopeToSelectedPeriod,
} from '@hooks/requests/queries/use_billing_trend_query';
import { useMessagesTotalQuery } from '@hooks/requests/queries/use_messages_total_query';
import { ErrorFallback } from '@components/feedback';
import { DateRangeFilter, DropdownFilter, getDefaultDateRange, LastUpdatedStatus } from '@ui';
import type { DateRangeValue } from '@ui';
import {
  AnalyticsSummaryWidgets,
  CompanyOverviewTable,
  DashboardSkeleton,
  UsageOverTimeChart,
  UsersByCountry,
} from '../components/dashboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { getOrganizationName } from '../components/dashboard/organization_label';
import { isProvisionedByPeriodEnd } from '../components/dashboard/date_range_label';

const UserAnalytics: React.FC = () => {
  // AceUp Admin/Viewer always has exactly one org selected — no "All" option.
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);

  const { isAceUpStaff, isPartnerRole, displayRole } = usePartnerRoles();

  const {
    data: users,
    isLoading: isUsersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useAllUsersQuery({
    // AceUp staff: scope to the org selected in the dropdown, and hold off
    // firing the request until one is selected (there's no "All orgs" view).
    // Partner roles: org comes from the JWT, so no param needed.
    organizationId: isAceUpStaff ? selectedOrganizationId : undefined,
    enabled: !isAceUpStaff || Boolean(selectedOrganizationId),
  });

  const { data: organizations } = usePartnerOrganizationsQuery(isAceUpStaff);
  // Partner roles only — resolved from the JWT's org_id claim, used for the
  // "Organization"/"Created on" header segments. AceUp staff pick an org from
  // the dropdown instead, so this stays disabled for them.
  const { data: ownOrganization } = usePartnerOrganizationQuery(isPartnerRole);
  // AceUp staff: scope directly to the selected org so the Company filter
  // dropdown reacts to org switches via a real request (the backend returns
  // an unscoped, cross-tenant list when this param is omitted, not the
  // selected org's companies).
  const { data: allCompanies } = usePartnerCompaniesAllQuery({
    organizationId: isAceUpStaff ? selectedOrganizationId : undefined,
    enabled: !isAceUpStaff || Boolean(selectedOrganizationId),
  });

  const allPartnerCompanies = allCompanies ?? [];

  // For AceUp staff, every company-derived list (dropdown, trend chart company
  // ids, etc.) must be scoped to the selected org — otherwise the company
  // filter would offer companies from orgs the user isn't even looking at.
  const partnerCompanies = useMemo(() => {
    if (!isAceUpStaff || !selectedOrganizationId) return allPartnerCompanies;
    return allPartnerCompanies.filter((c) => c.organizationId === selectedOrganizationId);
  }, [allPartnerCompanies, isAceUpStaff, selectedOrganizationId]);

  // Billing trend — use selected companies when filtered, otherwise all (org-scoped) companies
  const allCompanyIds = useMemo(() => [...new Set(partnerCompanies.map((c) => c.companyId))], [
    partnerCompanies,
  ]);
  const trendCompanyIds = selectedCompanyIds.length > 0 ? selectedCompanyIds : allCompanyIds;
  // AceUp staff: pass the selected org through — billing periods are keyed by
  // org + company, so without this the backend falls back to the caller's own
  // JWT org_id and every lookup for the *selected* org silently misses (flat
  // zeros, regardless of which org or date range is picked).
  const { trendData, isLoading: isTrendLoading } = useBillingTrendQuery(
    trendCompanyIds,
    dateRange,
    6,
    isAceUpStaff ? selectedOrganizationId : undefined
  );

  // Messages total — scoped to a single company when exactly one is selected
  const messageParams = useMemo(() => {
    const toDateStr = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;
    const companyId = selectedCompanyIds.length === 1 ? selectedCompanyIds[0] : undefined;
    if (!dateRange) return { companyId };
    return { from: toDateStr(dateRange.from), to: toDateStr(dateRange.to), companyId };
  }, [dateRange, selectedCompanyIds]);

  const { data: messagesData, isLoading: isMessagesLoading } = useMessagesTotalQuery(messageParams);

  const organizationOptions = useMemo(() => {
    if (!organizations?.length) return [];
    // The organizations API can return duplicate rows for the same org id —
    // dedupe so DropdownFilter never renders two options with the same key.
    const seen = new Set<string>();
    return organizations
      .filter((org) => {
        if (seen.has(org.organizationId)) return false;
        seen.add(org.organizationId);
        return true;
      })
      .map((org) => ({
        label: getOrganizationName(org.organizationName, org.organizationId),
        value: org.organizationId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [organizations]);

  // Default org for AceUp staff = the most recently added org.
  const defaultOrganizationId = useMemo(() => {
    if (!organizations?.length) return null;
    const mostRecent = organizations.reduce<typeof organizations[number] | null>((best, org) => {
      const bestTime = best?.organizationCreatedAt
        ? new Date(best.organizationCreatedAt).getTime()
        : -Infinity;
      const orgTime = org.organizationCreatedAt
        ? new Date(org.organizationCreatedAt).getTime()
        : -Infinity;
      return orgTime > bestTime ? org : best;
    }, null);
    return mostRecent?.organizationId ?? null;
  }, [organizations]);

  // AceUp Admin/Viewer must always have an org selected — select the default once orgs load.
  useEffect(() => {
    if (isAceUpStaff && selectedOrganizationId === null && defaultOrganizationId) {
      setSelectedOrganizationId(defaultOrganizationId);
    }
  }, [isAceUpStaff, selectedOrganizationId, defaultOrganizationId]);

  // The selected org's creation month bounds the date range picker — no months before the org joined.
  const selectedOrgMinMonth = useMemo(() => {
    if (!isAceUpStaff || !organizations) return null;
    const org = organizations.find((o) => o.organizationId === selectedOrganizationId);
    return org?.organizationCreatedAt ? new Date(org.organizationCreatedAt) : null;
  }, [isAceUpStaff, organizations, selectedOrganizationId]);

  // Reset client selection + date range (to current month) whenever the org changes.
  useEffect(() => {
    setSelectedCompanyIds([]);
    setDateRange(getDefaultDateRange(selectedOrgMinMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationId]);

  const companyOptions = useMemo(() => {
    if (!partnerCompanies.length) return [];

    const seen = new Set<string>();
    return partnerCompanies
      .map((c) => ({ label: c.companyName, value: c.companyId }))
      .filter((opt) => {
        if (seen.has(opt.value)) return false;
        seen.add(opt.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [partnerCompanies]);

  const isClientFilterDisabled =
    (isAceUpStaff && !selectedOrganizationId) || companyOptions.length === 0;

  const hasActiveFilters =
    (isAceUpStaff && !!defaultOrganizationId && selectedOrganizationId !== defaultOrganizationId) ||
    selectedCompanyIds.length > 0 ||
    (dateRange !== null && dateRange.preset !== 'current_month');

  const handleClearFilters = () => {
    if (isAceUpStaff && defaultOrganizationId) {
      setSelectedOrganizationId(defaultOrganizationId);
    }
    setSelectedCompanyIds([]);
    setDateRange(getDefaultDateRange(selectedOrgMinMonth));
  };

  const filteredUsers = useMemo(() => {
    // Org scoping now happens server-side (organization_id is sent for AceUp
    // staff and enforced from the JWT for Partner roles) — company + date
    // range filters are applied client-side.
    let result = users ?? [];

    // Scope "Total Users" to the selected period — a past selection counts
    // only users provisioned by that period's end, not the full current roster.
    result = result.filter((u) => isProvisionedByPeriodEnd(u.created_at, dateRange));

    if (selectedCompanyIds.length > 0) {
      const companySet = new Set(selectedCompanyIds);
      result = result.filter((u) => companySet.has(u.company_id));
    }

    return result;
  }, [users, selectedCompanyIds, dateRange]);

  // High-water mark: the month with the highest maxUserCount within the
  // *selected period* specifically — trendData itself may span a wider
  // trailing window than that (the chart keeps surrounding months for
  // context), so narrow it down first rather than reducing over all of it.
  // trendData is trusted as-is from the backend (via aggregateTrendData's
  // per-company carry-forward) — no client-side flooring against the
  // current user count.
  const hwmPoint = useMemo(
    () =>
      scopeToSelectedPeriod(trendData, dateRange).reduce<{
        maxUserCount: number;
        label: string;
      } | null>((best, p) => (p.maxUserCount > (best?.maxUserCount ?? -1) ? p : best), null),
    [trendData, dateRange]
  );

  if (isUsersLoading || (isAceUpStaff && !selectedOrganizationId)) {
    return <DashboardSkeleton />;
  }

  // No organization_id in this response (already in the caller's own JWT) —
  // unlike the AceUp-staff org list, there's no id to fall back to here.
  const ownOrganizationName = ownOrganization?.organizationName?.trim() || null;

  const ownOrganizationCreatedOn = ownOrganization?.organizationCreatedAt
    ? new Date(ownOrganization.organizationCreatedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const filterLabel = (() => {
    if (isAceUpStaff && selectedOrganizationId && organizations) {
      const org = organizations.find((o) => o.organizationId === selectedOrganizationId);
      return org ? getOrganizationName(org.organizationName, org.organizationId) : '1 partner';
    }
    return 'All partners';
  })();

  const clientLabel = (() => {
    // No selection, or every option explicitly selected, both mean "no
    // filtering applied" — show "All companies" either way.
    if (
      selectedCompanyIds.length === 0 ||
      (companyOptions.length > 0 && selectedCompanyIds.length === companyOptions.length)
    ) {
      return 'All companies';
    }
    if (selectedCompanyIds.length === 1 && partnerCompanies.length) {
      const company = partnerCompanies.find((c) => c.companyId === selectedCompanyIds[0]);
      return company?.companyName ?? '1 company';
    }
    return `${selectedCompanyIds.length} companies`;
  })();

  // Company dropdown's closed-box text: the library always renders
  // `filterLabel` verbatim for multi-selects (ignoring `value`), so build
  // it ourselves — first selected company's full name, plus a "+N" badge
  // for any additional selections. No selection shows "All". The field's
  // own label ("Company") sits above the control (DS `.au-field__label`).
  const companyFilterLabel = (() => {
    if (selectedCompanyIds.length === 0) return 'All';
    const firstCompany = partnerCompanies.find((c) => c.companyId === selectedCompanyIds[0]);
    const firstName = firstCompany?.companyName ?? selectedCompanyIds[0];
    const extraCount = selectedCompanyIds.length - 1;
    return (
      <>
        {firstName}
        {extraCount > 0 && <span className="au-company-filter-badge">{`+${extraCount}`}</span>}
      </>
    );
  })();

  return (
    <div className="ua-page">
      <div className="ua-page-head">
        <h1 className="ua-page-title">User Analytics</h1>
        <LastUpdatedStatus
          segments={[
            ...(isPartnerRole && ownOrganizationName
              ? [{ label: 'Organization', value: ownOrganizationName }]
              : []),
            ...(isPartnerRole && ownOrganizationCreatedOn
              ? [{ label: 'Created on', value: ownOrganizationCreatedOn }]
              : []),
          ]}
        />
      </div>

      <div className="ua-stack">
        {(isAceUpStaff || isPartnerRole) && (
          <div className="ua-filters">
            {isAceUpStaff && (
              <div className="ua-field">
                <span className="ua-field-label">Org</span>
                <DropdownFilter
                  filterLabel=""
                  name="organisation-filter"
                  required
                  options={organizationOptions}
                  value={selectedOrganizationId}
                  onChange={(_name: string, value: string[] | string | null) => {
                    const nextId = Array.isArray(value) ? (value[0] ?? null) : value;
                    if (nextId) setSelectedOrganizationId(nextId);
                  }}
                />
              </div>
            )}
            <div className="ua-field ua-field--company">
              <span className="ua-field-label">Company</span>
              <DropdownFilter
                filterLabel={companyFilterLabel}
                name="client-filter"
                isMulti
                disabled={isClientFilterDisabled}
                options={companyOptions}
                value={selectedCompanyIds}
                onChange={(_name: string, value: string[] | string | null) =>
                  setSelectedCompanyIds(Array.isArray(value) ? value : [])
                }
              />
            </div>
            <div className="ua-field ua-field--date-range">
              <span className="ua-field-label">Date range</span>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                minMonth={selectedOrgMinMonth}
              />
            </div>
            {hasActiveFilters && (
              <button type="button" className="ua-clear-filters" onClick={handleClearFilters}>
                <FontAwesomeIcon icon={faXmark} />
                Clear filters
              </button>
            )}
          </div>
        )}

        <AnalyticsSummaryWidgets
          totalUsers={filteredUsers.length}
          hwm={hwmPoint?.maxUserCount ?? 0}
          hwmMonth={hwmPoint?.label}
          totalMessages={messagesData?.totalMessages ?? null}
          isLoading={isTrendLoading || isMessagesLoading}
          orgLabel={isAceUpStaff ? filterLabel : undefined}
          companyLabel={clientLabel}
          dateRange={dateRange}
        />

        <UsageOverTimeChart
          billingData={trendData}
          orgLabel={isAceUpStaff ? filterLabel : undefined}
          companyLabel={clientLabel}
          isLoading={isTrendLoading}
        />

        <CompanyOverviewTable
          isAceUpStaff={isAceUpStaff}
          orgLabel={isAceUpStaff ? filterLabel : undefined}
          companyLabel={clientLabel}
          organizations={organizations}
          selectedOrganizationId={selectedOrganizationId}
          selectedCompanyIds={selectedCompanyIds}
          dateRange={dateRange}
        />

        {displayRole !== 'PartnerViewer' && (
          <UsersByCountry
            users={filteredUsers}
            orgLabel={isAceUpStaff ? filterLabel : undefined}
            companyLabel={clientLabel}
            dateRange={dateRange}
          />
        )}
      </div>
    </div>
  );
};

export default UserAnalytics;
